import React, { ReactNode, useState, useEffect } from "react";
import { Clock } from "lucide-react";
import ProviderSidebarV3 from "./ProviderSidebarV3";
import ErrorBoundaryV3 from "./ErrorBoundaryV3";
import MobileNav from "../MobileNav";

function getSecondsUntilNextActivation(): number {
  const now = new Date();
  const minutes = now.getMinutes();
  const seconds = now.getSeconds();
  const elapsedInHalf = (minutes % 30) * 60 + seconds;
  return 30 * 60 - elapsedInHalf;
}

function formatCountdownParts(totalSeconds: number): { minutes: string; seconds: string } {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return {
    minutes: String(m).padStart(2, "0"),
    seconds: String(s).padStart(2, "0"),
  };
}

function useActivationCountdown(): { minutes: string; seconds: string } {
  const [remaining, setRemaining] = useState(getSecondsUntilNextActivation);

  useEffect(() => {
    const id = setInterval(() => {
      setRemaining(getSecondsUntilNextActivation());
    }, 1000);
    return () => clearInterval(id);
  }, []);

  return formatCountdownParts(remaining);
}

interface ProviderLayoutV3Props {
  children: ReactNode;
}

const ProviderLayoutV3: React.FC<ProviderLayoutV3Props> = ({ children }) => {
  const countdown = useActivationCountdown();

  return (
    <div className="h-dvh bg-rp-black flex flex-col md:flex-row overflow-hidden">
      <MobileNav mode="provider" />
      <ProviderSidebarV3 />
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-16 border-b border-rp-separator flex items-center justify-between px-6 bg-rp-black/80 backdrop-blur shrink-0">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-bold text-rp-grey">Provider Portal</h2>
          </div>
          <div className="flex items-center gap-2 text-rp-grey">
            <Clock className="w-3.5 h-3.5 text-rp-grey/50" />
            <span className="text-xs text-white">Next activation window</span>
            <span className="font-mono text-sm font-bold tabular-nums tracking-tight animate-pulse">
              {countdown.minutes}:{countdown.seconds}
            </span>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          <ErrorBoundaryV3>{children}</ErrorBoundaryV3>
        </div>
      </div>
    </div>
  );
};

export default ProviderLayoutV3;
